// ── LOCALE PACK — the ONE copy/format layer ──────────────────────────────────
// Driven by the console-owned `language` (en-GB | en-US) and `country` (UK|US|NP)
// flags, hydrated once from /api/features alongside every other flag. There is no
// instance-name gate anywhere in here.
//
// DESIGN RULE — INERT BY CONSTRUCTION ON en-GB/metric:
//   Every export below returns EXACTLY what the pre-locale code returned when the
//   language is en-GB and the unit system is metric. `T()` returns its input by
//   identity; `fmtDate()` still emits "06 Jul 2023"; `fmtWeight()` still emits
//   "74.2 kg". That is what makes "UK and NPL rendered output unchanged" a
//   property of the code rather than a thing we have to re-test string by string.
//
// STORAGE IS NEVER TOUCHED. Weight stays kg and height stays cm in the DB, in the
// API, and in every calculation (BMI, PhenoAge, bio-age). Imperial exists only at
// the moment of render.

let _lang = 'en-GB';
let _country = 'UK';

// User override of the country default, persisted client-side. null = follow country.
const _UNIT_KEY = 'kalpa_unit_system';
let _unitOverride = null;
try {
  const s = localStorage.getItem(_UNIT_KEY);
  if (s === 'metric' || s === 'imperial') _unitOverride = s;
} catch (e) { /* private mode / no storage — fall back to the country default */ }

const _LISTENERS = new Set();
function _emit() { _LISTENERS.forEach(fn => { try { fn(); } catch (e) { /* one bad listener must not stop the rest */ } }); }

export function onLocaleChange(fn) { _LISTENERS.add(fn); return () => _LISTENERS.delete(fn); }

/** Called once from the /api/features hydration. Safe to call repeatedly. */
export function setLocale(language, country) {
  _lang = (language === 'en-US') ? 'en-US' : 'en-GB';
  _country = String(country || 'UK').toUpperCase();
  _emit();
}

export function getLanguage() { return _lang; }
export function getCountry() { return _country; }

// ── Unit system ──────────────────────────────────────────────────────────────
/** 'metric' | 'imperial'. Country supplies the DEFAULT; the user's toggle wins. */
export function unitSystem() {
  if (_unitOverride) return _unitOverride;
  return _country === 'US' ? 'imperial' : 'metric';
}
export function isImperial() { return unitSystem() === 'imperial'; }
/** Toggle target: pass 'metric' | 'imperial', or null to go back to the country default. */
export function setUnitSystem(sys) {
  _unitOverride = (sys === 'metric' || sys === 'imperial') ? sys : null;
  try {
    if (_unitOverride) localStorage.setItem(_UNIT_KEY, _unitOverride);
    else localStorage.removeItem(_UNIT_KEY);
  } catch (e) { /* ignore — the in-memory value still drives this session */ }
  _emit();
}
/** True when the instance's country makes a units toggle meaningful at all. */
export function unitsToggleAvailable() { return _country === 'US'; }

const _LB_PER_KG = 2.2046226218;
const _IN_PER_CM = 0.3937007874;

/** kg (storage) -> display string. Metric: "74.2 kg". Imperial: "163.6 lb". */
export function fmtWeight(kg, opts) {
  const n = Number(kg);
  if (kg == null || kg === '' || isNaN(n)) return '';
  const dp = (opts && opts.decimals != null) ? opts.decimals : 1;
  if (isImperial()) return `${(n * _LB_PER_KG).toFixed(dp)} lb`;
  return `${n.toFixed(dp)} kg`;
}
/** A weight DELTA (already a kg difference) -> display string, sign left to the caller. */
export function fmtWeightDelta(kgDelta, opts) {
  const n = Number(kgDelta);
  if (kgDelta == null || isNaN(n)) return '';
  const dp = (opts && opts.decimals != null) ? opts.decimals : 1;
  if (isImperial()) return `${(n * _LB_PER_KG).toFixed(dp)} lb`;
  return `${n.toFixed(dp)} kg`;
}
/** Just the unit word, for chart axes / table headers. */
export function weightUnit() { return isImperial() ? 'lb' : 'kg'; }
export function heightUnit() { return isImperial() ? 'ft/in' : 'cm'; }

/** cm (storage) -> display string. Metric: "178 cm". Imperial: "5' 10\"". */
export function fmtHeight(cm) {
  const n = Number(cm);
  if (cm == null || cm === '' || isNaN(n)) return '';
  if (isImperial()) {
    const totalIn = Math.round(n * _IN_PER_CM);
    const ft = Math.floor(totalIn / 12);
    const inch = totalIn % 12;
    return `${ft}' ${inch}"`;
  }
  return `${Math.round(n)} cm`;
}

/** Display-unit -> kg, for a user-entered weight. Inverse of fmtWeight's unit choice. */
export function weightInputToKg(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return NaN;
  return isImperial() ? n / _LB_PER_KG : n;
}
/** kg -> the number to PREFILL a weight input with (bare number, no unit). */
export function kgToWeightInput(kg, decimals) {
  const n = Number(kg);
  if (kg == null || isNaN(n)) return '';
  const dp = decimals == null ? 1 : decimals;
  return (isImperial() ? n * _LB_PER_KG : n).toFixed(dp);
}

// ── Dates ────────────────────────────────────────────────────────────────────
const _MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// A bare 'YYYY-MM-DD' is reformatted purely as a STRING so a UTC parse can never roll
// the day back in +05:45 (Nepal) — the day the lab printed is the day shown. Anything
// else goes through Date. (Carried over verbatim from the original formatDate.)
function _parts(input) {
  if (input == null || input === '') return null;
  if (typeof input === 'string') {
    const m = input.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const mi = parseInt(m[2], 10);
      if (mi >= 1 && mi <= 12) return { y: m[1], m: mi, d: m[3] };
    }
  }
  const dt = input instanceof Date ? input : new Date(input);
  if (isNaN(dt.getTime())) return null;
  return { y: String(dt.getFullYear()), m: dt.getMonth() + 1, d: String(dt.getDate()).padStart(2, '0') };
}

/** READABLE date. en-GB: "06 Jul 2023". en-US: "Jul 6, 2023". */
export function fmtDate(input) {
  if (input == null || input === '') return '';
  const p = _parts(input);
  if (!p) return String(input);
  if (_lang === 'en-US') return `${_MONS[p.m - 1]} ${parseInt(p.d, 10)}, ${p.y}`;
  return `${p.d} ${_MONS[p.m - 1]} ${p.y}`;
}

/** NUMERIC date, for DOB and dense table cells. en-GB: "22/12/1970". en-US: "12/22/1970". */
export function fmtDateNumeric(input) {
  if (input == null || input === '') return '';
  const p = _parts(input);
  if (!p) return String(input);
  const mm = String(p.m).padStart(2, '0');
  return _lang === 'en-US' ? `${mm}/${p.d}/${p.y}` : `${p.d}/${mm}/${p.y}`;
}

/** SHORT readable date, for inline labels. en-GB: "23 Jul". en-US: "Jul 23". The year is
 *  appended only when it is NOT the current year, so a routine label stays short but a
 *  genuinely old date can never be mistaken for a recent one. */
export function fmtDateShort(input, todayYear) {
  if (input == null || input === '') return '';
  const p = _parts(input);
  if (!p) return String(input);
  const cy = String(todayYear != null ? todayYear : new Date().getFullYear());
  const mon = _MONS[p.m - 1];
  const day = parseInt(p.d, 10);
  if (_lang === 'en-US') return p.y === cy ? `${mon} ${day}` : `${mon} ${day}, ${p.y}`;
  return p.y === cy ? `${p.d} ${mon}` : `${p.d} ${mon} ${p.y}`;
}

/** Rewrite ISO dates embedded in a SERVER-generated string into the locale's readable form. */
export function deIsoDatesLocale(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (whole, y, m, d) => {
    const mi = parseInt(m, 10);
    if (mi < 1 || mi > 12) return `${d}-${m}-${y}`;
    return _lang === 'en-US'
      ? `${_MONS[mi - 1]} ${parseInt(d, 10)}, ${y}`
      : `${d} ${_MONS[mi - 1]} ${y}`;
  });
}

// ── Clinical units (2f) ──────────────────────────────────────────────────────
// DISPLAY ONLY. Storage, the API payload and every scoring input (PhenoAge's nine, IRS-A,
// IRS-B) are untouched — this converts at the moment of render and nowhere else.
//
// SCOPE CORRECTION: the F6 survey concluded units were "mostly US-conventional" with "NO
// conversion layer" needed. That holds for the lab-native mg/dl, u/l and x10^3/ul values,
// but a direct read of the US database found three markers stored in SI units that a US
// reader would misread outright:
//
//   Vitamin D (25-OH)   nmol/L -> ng/mL   (/2.496)   75.4 nmol/L is 30 ng/mL: "adequate",
//                                                    yet 75.4 read as ng/mL looks excellent
//   Total Bilirubin     umol/L -> mg/dL   (/17.104)
//   TIBC                umol/L -> ug/dL   (*5.587)
//
// Sodium and Potassium are NOT converted: US mEq/L is numerically identical to mmol/L, so a
// "conversion" there would change the unit label while leaving the number alone — pure noise.
// NONE of the three is a PhenoAge input, so no score can move; that is asserted in the run.
const _UNIT_CONVERSIONS = [
  { match: /vitamin\s*d/i,        from: /^nmol\/l$/i,  to: 'ng/mL',  f: v => v / 2.496,  dp: 1 },
  { match: /bilirubin/i,          from: /^umol\/l$/i,  to: 'mg/dL',  f: v => v / 17.104, dp: 2 },
  { match: /^tibc$|iron.binding/i, from: /^umol\/l$/i, to: 'ug/dL',  f: v => v * 5.587,  dp: 0 },
];

// ── UK / NHS display conversions (Phase 4, 9 Aug 2026) ──────────────────────
// The UK panel is lab-native US-conventional (mg/dl, g/dl, ug/dl, x10^3/ul from the NPL
// pipeline). NHS convention is SI: glucose mmol/L, HbA1c mmol/mol (IFCC), creatinine
// umol/L, lipids mmol/L, Hb/albumin g/L, counts ×10⁹/L. DISPLAY ONLY, exactly like the
// US table above: storage, the API payload and every scoring input are untouched.
// Every rule is gated on BOTH the marker name AND the stored FROM unit, so a value
// already stored in SI is never double-converted.
// Factors: AACC/NIST SI conversion tables; HbA1c per the IFCC-NGSP master equation
// (mmol/mol = (% − 2.15) × 10.929).
const _UK_UNIT_CONVERSIONS = [
  { match: /hba1c|glycated|a1c\b/i,               from: /^%$/,                      to: 'mmol/mol', f: v => (v - 2.15) * 10.929, dp: 0 },
  { match: /glucose/i,                            from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.0555,  dp: 1 },
  { match: /creatinine/i,                         from: /^mg\/dl$/i,                to: 'umol/L',  f: v => v * 88.42,   dp: 0 },
  { match: /cholesterol|^hdl$|^ldl$|^vldl$|non.?hdl/i, from: /^mg\/dl$/i,           to: 'mmol/L',  f: v => v * 0.02586, dp: 1 },
  { match: /triglycer/i,                          from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.01129, dp: 1 },
  { match: /bilirubin/i,                          from: /^mg\/dl$/i,                to: 'umol/L',  f: v => v * 17.104,  dp: 0 },
  { match: /uric\s*acid|urate/i,                  from: /^mg\/dl$/i,                to: 'umol/L',  f: v => v * 59.48,   dp: 0 },
  { match: /calcium/i,                            from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.2495,  dp: 2 },
  { match: /phosph(at|or)/i,                      from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.3229,  dp: 2 },
  { match: /magnesium/i,                          from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.4114,  dp: 2 },
  { match: /^bun$|blood\s*urea|urea/i,            from: /^mg\/dl$/i,                to: 'mmol/L',  f: v => v * 0.357,   dp: 1 },
  { match: /albumin|total\s*protein|globulin/i,   from: /^g\/dl$/i,                 to: 'g/L',     f: v => v * 10,      dp: 0 },
  { match: /h(a?)emoglobin/i,                     from: /^g\/dl$/i,                 to: 'g/L',     f: v => v * 10,      dp: 0 },
  { match: /^iron$|tibc|iron.binding/i,           from: /^(u|mc)g\/dl$/i,           to: 'umol/L',  f: v => v * 0.179,   dp: 1 },
  { match: /ferritin/i,                           from: /^ng\/ml$/i,                to: 'ug/L',    f: v => v,           dp: null },
  { match: /vitamin\s*d/i,                        from: /^ng\/ml$/i,                to: 'nmol/L',  f: v => v * 2.496,   dp: 0 },
  { match: /b12/i,                                from: /^pg\/ml$/i,                to: 'ng/L',    f: v => v,           dp: null },
  { match: /crp|c.?reactive/i,                    from: /^mg\/dl$/i,                to: 'mg/L',    f: v => v * 10,      dp: 1 },
  { match: /testosterone/i,                       from: /^ng\/dl$/i,                to: 'nmol/L',  f: v => v * 0.0347,  dp: 1 },
  // Cell counts: numerically identical, NHS prints ×10⁹/L / ×10¹²/L. Any marker name.
  { match: /./,                                   from: /^x?10\^?3\/ul$/i,          to: '×10⁹/L',  f: v => v,           dp: null },
  { match: /./,                                   from: /^x?10\^?6\/ul$/i,          to: '×10¹²/L', f: v => v,           dp: null },
];

function _conversionRulesForCountry() {
  if (_country === 'US') return _UNIT_CONVERSIONS;
  if (_country === 'UK') return _UK_UNIT_CONVERSIONS;
  return null;   // NP and anything else: typography only, exactly as before
}

function _findRule(name, unit) {
  const rules = _conversionRulesForCountry();
  if (!rules) return null;
  const u = String(unit || '').trim();
  for (const c of rules) {
    if (c.match.test(String(name || '')) && c.from.test(u)) return c;
  }
  return null;
}

function _applyRule(c, value) {
  const n = c.f(Number(value));
  return c.dp == null ? n : Number(n.toFixed(c.dp));
}

// Typography only — same quantity, correct US rendering. "mg/dl" and "u/l" are not wrong so
// much as unprofessional on a document a clinician or underwriter reads.
const _UNIT_CASING = {
  'mg/dl': 'mg/dL', 'g/dl': 'g/dL', 'ng/dl': 'ng/dL', 'ug/dl': 'ug/dL', 'mcg/dl': 'mcg/dL',
  'mg/l': 'mg/L', 'g/l': 'g/L', 'ng/ml': 'ng/mL', 'iu/ml': 'IU/mL', 'iu/l': 'IU/L',
  'u/l': 'U/L', 'fl': 'fL', 'pg': 'pg', 'mmol/l': 'mmol/L', 'umol/l': 'umol/L',
  'nmol/l': 'nmol/L', 'x10^3/ul': 'x10³/uL', 'x10^6/ul': 'x10⁶/uL',
  'mm hg': 'mmHg', 'mm/hg': 'mmHg',
};

/** Normalise a unit's TYPOGRAPHY without touching the quantity. Safe in every locale. */
export function normaliseUnit(unit) {
  if (!unit) return unit;
  const k = String(unit).trim().toLowerCase();
  return _UNIT_CASING[k] || unit;
}

/**
 * Convert a marker value for DISPLAY. Returns {value, unit, converted, from}.
 * US: the three SI->US rules. UK: the NHS/SI table. NP: identity apart from unit typography.
 */
export function displayMarker(name, value, unit) {
  const out = { value, unit: normaliseUnit(unit), converted: false, from: null };
  if (value == null || value === '' || isNaN(Number(value))) return out;
  const c = _findRule(name, unit);
  if (!c) return out;
  return { value: _applyRule(c, value), unit: c.to, converted: true, from: String(unit) };
}

/** Convert a marker's reference bounds with the SAME rule as its value, so a value and its
 *  range can never render in different units. Returns {low, high, unit, converted}. */
export function displayRange(name, low, high, unit) {
  const c = _findRule(name, unit);
  if (!c) return { low, high, unit: normaliseUnit(unit), converted: false };
  const cv = (x) => (x == null || x === '' || isNaN(Number(x))) ? x : _applyRule(c, x);
  return { low: cv(low), high: cv(high), unit: c.to, converted: true };
}

/**
 * Convert the /clinical-timeline/panels payload IN PLACE for display (Phase 4).
 * One choke point: every consumer of that payload — Health Record panels, marker drawers,
 * range bars, sparklines, tooltips, insight charts — then renders value, unit, range and
 * history in the SAME unit system, so a chart axis can never disagree with its headline.
 * Statuses are NOT recomputed: the conversions are linear, so in/out-of-range is invariant
 * (and the server computed status from the stored values anyway).
 * The ORIGINAL values stay available at m._stored {value, unit, history} for any consumer
 * that computes in stored units (eGFR expects creatinine mg/dL — see the call site).
 */
export function convertPanelsPayload(data) {
  const panels = data && (Array.isArray(data.panels) ? data.panels : (Array.isArray(data) ? data : null));
  if (!panels) return data;
  for (const p of panels) {
    for (const m of (p.markers || [])) {
      const c = _findRule(m.displayName || m.name, m.unit);
      if (!c) { m.unit = normaliseUnit(m.unit); continue; }
      m._stored = {
        unit: m.unit,
        value: m.latest ? m.latest.value : null,
        history: (m.history || []).map(h => ({ date: h.date, value: h.value, unit: h.unit })),
      };
      const cv = (x) => (x == null || x === '' || isNaN(Number(x))) ? x : _applyRule(c, x);
      m.unit = c.to;
      if (m.refLow != null) m.refLow = cv(m.refLow);
      if (m.refHigh != null) m.refHigh = cv(m.refHigh);
      if (m.optimalLow != null) m.optimalLow = cv(m.optimalLow);
      if (m.optimalHigh != null) m.optimalHigh = cv(m.optimalHigh);
      if (m.latest && m.latest.value != null) {
        // Convert an entry only when ITS OWN unit matches the rule (or it has none and
        // inherits the marker unit) — a history row already stored in SI must not convert.
        if (!m.latest.unit || c.from.test(String(m.latest.unit).trim())) {
          m.latest.value = cv(m.latest.value);
          if (m.latest.unit) m.latest.unit = c.to;
        }
      }
      for (const h of (m.history || [])) {
        if (h.value != null && (!h.unit || c.from.test(String(h.unit).trim()))) {
          h.value = cv(h.value);
          if (h.unit) h.unit = c.to;
        }
      }
    }
  }
  return data;
}

// ── KAL-029: unit-bearing PROSE ─────────────────────────────────────────────
// The governed insight sentences are composed server-side and embed the value AND the
// stored unit as text ("has fallen from 49.1 ng/mL to 27 ng/mL"). convertFlagsPayload
// moves the numeric fields, so on UK the card read "27 ug/L" while the sentence beneath
// it still said "27 ng/mL" — the same measurement quoted two ways on one card.
//
// THE RULE IS NOT RE-DERIVED HERE. We rewrite prose with EXACTLY the rule that was applied
// to that flag's own value, so prose can never disagree with the number above it, and a
// marker the table does not convert (AST U/L, MCH pg) is left completely alone.
function _escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/**
 * Rewrite "<number> <fromUnit>" occurrences in one prose string using conversion rule `c`.
 * Returns the input unchanged when there is nothing to do. Percent units are matched with
 * no separating space ("5.2%"); everything else allows optional whitespace.
 */
export function convertProseUnits(text, c) {
  if (!text || typeof text !== 'string' || !c) return text;
  // The rule's `from` is an anchored unit matcher; recover the literal unit text as it is
  // actually written in the sentence by testing the candidates we have seen stored.
  const src = c.from.source.replace(/^\^|\$$/g, '');
  const isPct = src === '%' || src === '\\%';
  const unitPat = isPct ? '%' : src;
  const sep = isPct ? '\\s*' : '\\s*';
  const re = new RegExp('(^|[^0-9A-Za-z.])(\\d+(?:\\.\\d+)?)' + sep + '(' + unitPat + ')(?![0-9A-Za-z])', 'gi');
  return text.replace(re, (whole, pre, num) => {
    const v = _applyRule(c, Number(num));
    if (v == null || isNaN(Number(v))) return whole;
    return pre + v + (isPct ? ' ' : ' ') + c.to;
  });
}

/**
 * Convert the /health-record/longevity-flags payload IN PLACE for display (Phase 4).
 * Same contract as convertPanelsPayload: one choke point, value/range/history/supporting
 * all move together, statuses are server-computed and untouched. The double-conversion
 * hazard is structurally impossible — every rule is gated on its FROM unit, so a payload
 * already converted here no longer matches any rule at a render site (mkVal).
 * KNOWN LIMIT: backend-governed insight SENTENCES (flag.insight.*) can cite values in
 * stored units; prose is not rewritten. Registered as follow-up.
 */
export function convertFlagsPayload(data) {
  const flags = data && (Array.isArray(data.flags) ? data.flags : (Array.isArray(data) ? data : null));
  if (!flags) return data;
  for (const f of flags) {
    if (!f || typeof f !== 'object') continue;
    const name = f.display_name || f.displayName || f.marker_name || f.name || f.marker_key;
    const c = _findRule(name, f.unit);
    if (c) {
      const cv = (x) => (x == null || x === '' || isNaN(Number(x))) ? x : _applyRule(c, x);
      f._stored = { unit: f.unit, value: f.current_value };
      f.unit = c.to;
      for (const k of ['current_value', 'ref_min', 'ref_max', 'optimal_min', 'optimal_max']) {
        if (f[k] != null) f[k] = cv(f[k]);
      }
      for (const h of (f.history || [])) {
        if (h && h.value != null && (!h.unit || c.from.test(String(h.unit).trim()))) {
          h.value = cv(h.value);
          if (h.unit) h.unit = c.to;
        }
      }
      // KAL-029 — the governed prose quotes the same measurement; rewrite it with the SAME
      // rule so the sentence cannot contradict the value rendered above it.
      const ins = f.insight;
      if (ins && typeof ins === 'object') {
        for (const k of ['trend_sentence', 'cross_marker', 'meds_note', 'diet_context']) {
          if (typeof ins[k] === 'string') ins[k] = convertProseUnits(ins[k], c);
        }
      }
    } else if (f.unit) {
      f.unit = normaliseUnit(f.unit);
    }
    const s = f.supporting;
    if (s && typeof s === 'object') {
      const sc = _findRule(s.label || name, s.unit);
      if (sc) {
        const scv = (x) => (x == null || x === '' || isNaN(Number(x))) ? x : _applyRule(sc, x);
        for (const k of ['value', 'ref_min', 'ref_max']) {
          if (s[k] != null) s[k] = scv(s[k]);
        }
        s.unit = sc.to;
      } else if (s.unit) {
        s.unit = normaliseUnit(s.unit);
      }
    }
  }
  return data;
}

// ── Spelling (copy layer) ────────────────────────────────────────────────────
// CURATED, not a general -ise/-ize rewriter: an automatic rule would corrupt words
// that are correct in BOTH locales (advertise, exercise, promise, comprise, rise)
// and proper nouns. This list is exactly the set that a word-boundary audit found
// in user-visible copy, and nothing else is touched.
//
// NOT IN HERE, DELIBERATELY: the string 'analysed' where it is a STATE TOKEN
// (upload phase keys, `mark(i,'analysed')`). Those are program values, not copy,
// and are never passed through T().
// STEM rules: [gbStem, usStem, suffixAlternation]. The suffix is preserved verbatim, so one
// entry covers analyse/analysed/analyses/analysing without the ordering hazards of a flat
// word list. Anchored with \b on both ends.
//
// SCOPE NOTE (2d): the brief supplied a 9-word list (personalise/analyse/organise/programme/
// summarise). That list was produced by a FIXED-WORD \b sweep, which structurally cannot see
// suffixed forms — \boptimise\b does not match "optimisation" — and never covered the clinical
// ae/oe family at all. A pattern-based re-audit of the SERVED bundle found haemoglobin x21,
// plus haematocrit/haematology/anaemia/fibre/metre/favour/defence/grey/ageing/prioritise/
// metabolise/optimisation/normalisation/localise/characterise/authorised/anonymised/
// randomisation. On a blood-results product being shown to a US insurer, "haemoglobin" is the
// single most visible word we could get wrong, so the map below is the re-audited set.
const _STEMS = [
  ['personalis', 'personaliz', 'e|ed|es|ing|ation'],
  ['analys',     'analyz',     'e|ed|es|ing'],
  ['organis',    'organiz',    'e|ed|es|ing|ation'],
  ['summaris',   'summariz',   'e|ed|es|ing|ation'],
  ['prioritis',  'prioritiz',  'e|ed|es|ing|ation'],
  ['optimis',    'optimiz',    'e|ed|es|ing|ation'],
  ['metabolis',  'metaboliz',  'e|ed|es|ing|ation|er|ers'],
  ['normalis',   'normaliz',   'e|ed|es|ing|ation'],
  ['localis',    'localiz',    'e|ed|es|ing|ation'],
  ['characteris', 'characteriz', 'e|ed|es|ing|ation'],
  ['authoris',   'authoriz',   'e|ed|es|ing|ation'],
  ['anonymis',   'anonymiz',   'e|ed|es|ing|ation'],
  ['utilis',     'utiliz',     'e|ed|es|ing|ation'],
  ['randomis',   'randomiz',   'e|ed|es|ing|ation'],
  ['visualis',   'visualiz',   'e|ed|es|ing|ation'],
  ['neutralis',  'neutraliz',  'e|ed|es|ing|ation'],
  ['emphasis',   'emphasiz',   'ed|es|ing'],   // NOT bare "emphasis" — that is the same in both
  ['customis',   'customiz',   'e|ed|es|ing|ation'],
  ['recognis',   'recogniz',   'e|ed|es|ing'],
  ['minimis',    'minimiz',    'e|ed|es|ing'],
  ['maximis',    'maximiz',    'e|ed|es|ing'],
  ['stabilis',   'stabiliz',   'e|ed|es|ing'],
  // Clinical ae/oe — the family the original audit never checked.
  ['haemoglobin', 'hemoglobin', 's?'],
  ['haematocrit', 'hematocrit', 's?'],
  ['haematolog',  'hematolog',  'y|ical|ically'],
  ['anaemi',      'anemi',      'a|c|as'],
  ['oedema',      'edema',      's?'],
  ['paediatric',  'pediatric',  's?'],
  ['ischaemi',    'ischemi',    'a|c'],
  ['oesophag',    'esophag',    'us|eal'],
  ['leukaemi',    'leukemi',    'a|as'],
  ['diarrhoea',   'diarrhea',   ''],
  ['coeliac',     'celiac',     ''],
  ['foetal',      'fetal',      ''],
  // -re / -our / misc, restricted to forms actually present in the product's copy.
  ['fibre',       'fiber',      's?'],
  ['metre',       'meter',      's?'],
  ['litre',       'liter',      's?'],
  ['favour',      'favor',      's|ed|ing|able|ably|'],
  ['behaviour',   'behavior',   's|al|ally|'],
  ['colour',      'color',      's|ed|ing|'],
  ['defence',     'defense',    's?'],
  ['programme',   'program',    's?'],
  ['grey',        'gray',       's|ed|ing|'],
  ['ageing',      'aging',      ''],
  ['practis',     'practic',    'e|ed|es|ing'],
  ['modelling',   'modeling',   ''],
  ['labelling',   'labeling',   ''],
  ['whilst',      'while',      ''],
  ['amongst',     'among',      ''],
];

// Case-preserving: "Analysing" -> "Analyzing", "ANALYSING" -> "ANALYZING".
function _matchCase(src, repl) {
  if (src === src.toUpperCase() && src !== src.toLowerCase()) return repl.toUpperCase();
  if (src[0] === src[0].toUpperCase()) return repl[0].toUpperCase() + repl.slice(1);
  return repl;
}

const _RULES = _STEMS.map(([gb, us, sfx]) => ({
  re: new RegExp(`\\b(${gb})(${sfx})\\b`, 'gi'),
  gb, us,
}));

/**
 * THE copy-layer entry point. en-GB: returns its argument unchanged (identity).
 * en-US: applies the curated spelling map, preserving case.
 * Null-safe and non-string-safe so it can wrap any expression at a render site.
 */
export function T(s) {
  if (_lang !== 'en-US') return s;
  if (!s || typeof s !== 'string') return s;
  let out = s;
  for (const r of _RULES) {
    out = out.replace(r.re, (whole, stem, sfx) => _matchCase(stem, r.us) + sfx);
  }
  return out;
}

/** Tagged-template form, for interpolated copy: T2`Analysing ${name}…` */
export function T2(strings, ...vals) {
  return T(strings.reduce((acc, s, i) => acc + s + (i < vals.length ? String(vals[i] ?? '') : ''), ''));
}
